import { useMemo } from 'react'
import { useAuditEvents } from './useAuditEvents'
import type { AnalysisTimeRangeKey } from '../../../../types/analysis'
import type { LiveModuleHeadline, LiveModuleRecord, LiveModuleSparkline } from '../LiveModuleCard'
import { bucketize, calculateDelta } from './bucketize'

export type UseMeetingsReadinessModuleParams = {
  workspaceId: string
  timeRange?: AnalysisTimeRangeKey
  refreshKey?: number
  now?: Date
}

export type UseMeetingsReadinessModuleResult = {
  headline: LiveModuleHeadline
  sparkline: LiveModuleSparkline
  records: LiveModuleRecord[]
  loading: boolean
  error: Error | null
}

const daysSince = (date: Date | null, now: Date) => date ? Math.max(0, Math.floor((now.getTime() - date.getTime()) / 86_400_000)) : 0

const isMeetingAlert = (category: string) => category === 'MEETING_READINESS_ALERT'

export const useMeetingsReadinessModule = ({
  workspaceId,
  timeRange = '7D',
  refreshKey,
  now = new Date(),
}: UseMeetingsReadinessModuleParams): UseMeetingsReadinessModuleResult => {
  const { records: auditRecords, loading, error } = useAuditEvents({ workspaceId, timeRange, refreshKey })

  return useMemo(() => {
    const alerts = auditRecords.filter((record) => isMeetingAlert(record.auditCategory))

    if (alerts.length === 0) {
      return {
        headline: { value: 0, label: 'alerts' },
        sparkline: { points: [], direction: 'up-is-bad' },
        records: [],
        loading,
        error,
      }
    }

    const points = bucketize(alerts.map((alert) => alert.createdAt), timeRange, now)
    const records: LiveModuleRecord[] = [...alerts]
      .sort((left, right) => (right.createdAt?.getTime() ?? 0) - (left.createdAt?.getTime() ?? 0))
      .slice(0, 5)
      .map((alert, index) => ({
        id: alert.id || `meeting-readiness-${index}`,
        primary: alert.targetTitle?.trim() || 'Readiness alert',
        secondary: alert.summary?.trim() || alert.metadata?.providerName?.toString() || 'Meeting readiness context',
        numeric: daysSince(alert.createdAt, now),
        drillTarget: 'MEETINGS',
      }))

    // More readiness alerts indicate a degrading meeting posture, so up is bad.
    return {
      headline: { value: alerts.length, label: 'alerts', delta: calculateDelta(points) },
      sparkline: { points, direction: 'up-is-bad' },
      records,
      loading,
      error,
    }
  }, [auditRecords, timeRange, now, loading, error])
}
